<template>
  <div id="assignment-performance">
    <el-row>
      <el-col>
        <el-table
          v-loading.fullscreen.lock="isModalLoading"
          :border="false"
          :cell-class-name="getCellClass"
          :data="performances"
          :default-sort="{ prop: 'score', order: 'descending' }"
          :empty-text="$t('common.no_values')"
          :max-height="600"
          style="width: 100%"
          @cell-mouse-enter="handleHoveringCell"
          @cell-mouse-leave="clearHoveringCell"
        >
          <el-table-column
            :label="$t('school_labels.student_name')"
            :sort-method="sortName"
            :sort-orders="getSortOrders"
            fixed
            min-width="180"
            sortable
          >
            <template slot-scope="scope">
              <div class="user-info">
                <div class="owner-info">
                  <el-link @click="setAssignmentDateAndRedirect(scope.row)" :underline="false">
                    <p>{{ scope.row.user_name }}</p>
                    <p>{{ scope.row.user_email }}</p>
                  </el-link>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('assignments.details.performance_date_complete')"
            :render-header="handleHelpTooltip"
            :sort-name="sortName"
            :sort-orders="getSortOrders"
            fixed
            prop="date_complete"
            sortable
            min-width="180"
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.date_complete) }}
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('assignments.details.highest_assignment_score')"
            :render-header="handleHelpTooltip"
            :sort-orders="getSortOrders"
            align="center"
            fixed
            min-width="160"
            prop="score"
            sortable
          >
            <template slot-scope="scope">
              <span>
                <span>{{ formatScore(scope.row.score) }}</span>
                <el-button
                  v-if="canShowButtonHistoryScore(scope.row)"
                  class="assignment-detail-btn"
                  @click.native.prevent="handleClickHistory(scope.row)"
                >
                  {{ $t("classes.classes_details.assessment.table.btn_history") }}
                </el-button>
              </span>
            </template>
          </el-table-column>
          <template v-if="isStudySet">
            <el-table-column
              v-for="(ss_id, idx) in studySets"
              :key="idx"
              :label="'Study set ' + (idx + 1) + ' High Score'"
              align="center"
              class-name="activity-cell"
              width="150"
            >
              <template slot-scope="scope">
                {{ getHighestScore(ss_id, scope) }}
              </template>
            </el-table-column>
          </template>
          <template v-else>
            <el-table-column
              v-if="studySets.length > 0"
              :label="$t('assignments.details.warm_up_study_set_high_score')"
              align="center"
              min-width="150"
            >
              <template slot-scope="scope">
                <span>
                  {{ getWarmUpSSScore(scope.row) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('assignments.details.read_aloud_overall_high_score')"
              align="center"
              min-width="150"
            >
              <template slot-scope="scope">
                <span>
                  {{ getRAHighestScore(scope.row, "highest_score") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('assignments.details.eps_high_score')" align="center" min-width="150">
              <template slot-scope="scope">
                <span>
                  {{ getRAHighestScore(scope.row, "highest_score") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('assignments.details.fluency_high_score')" align="center" min-width="150">
              <template slot-scope="scope">
                <span>
                  {{ getRAHighestScore(scope.row, "highest_fluency_score") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('assignments.details.pronunciation_high_score')" align="center" min-width="150">
              <template slot-scope="scope">
                <span>
                  {{ getRAHighestScore(scope.row, "highest_pronunciation_score") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('assignments.details.intonation_high_score')" align="center" min-width="150">
              <template slot-scope="scope">
                <span>
                  {{ getRAHighestScore(scope.row, "highest_intonation_score") }}
                </span>
              </template>
            </el-table-column>
          </template>
        </el-table>
      </el-col>
    </el-row>
    <user-assignment-history
      :assignment-type="assignmentType"
      :dialogVisible="showUserHistoryScoreModal"
      :history="selectedUserHistoryScore"
      :title="$t('assignments.modals.assignment_history', { title: currentAssignment.name })"
      :user-info="selectedUser"
      :hasWarmUpSS="hasWarmUpSS"
      @closed="showUserHistoryScoreModal = false"
    />
  </div>
</template>

<script>
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { mapActions, mapGetters } from "vuex";
import { DATE_FORMAT, SUBMISSION_ON_TIME } from "@/constants/generic";
import { warningMessage } from "@/utils/messages";
import UserAssignmentHistory from "@/components/assignments/UserAssignmentHistory.vue";

export default {
  components: { UserAssignmentHistory },
  props: {
    performances: {
      type: Array,
      default: () => [{}],
    },
    dateFormat: {
      type: String,
      default: DATE_FORMAT,
    },
    studySets: {
      type: Array,
      default: () => [{}],
    },
    assignmentType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showUserHistoryScoreModal: false,
      selectedUserHistoryScore: {},
      selectedUser: {},
      hoveringId: "",
      isModalLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      currentAssignment: "assignments/getCurrentAssignment",
      currentClass: "classes/getCurrentClass",
      currentOrganization: "organization/currentOrganization",
    }),
    getSortOrders() {
      return ["ascending", "descending"];
    },
    isStudySet() {
      return this.assignmentType === "study_set";
    },
    hasWarmUpSS() {
      return !this.isStudySet && this.studySets.length > 0;
    },
  },
  methods: {
    ...mapActions({
      getUserAssignmentHistory: "assignments/getUserAssignmentHistory",
      setChartDate: "students/setChartDate",
    }),
    formatDate(date) {
      return isNaN(Date.parse(date)) ? date : format(new Date(date), this.dateFormat);
    },
    formatDateWithTimezone(date) {
      if (!date) return "--";
      let timeZone = this.currentOrganization.timezone;
      return utcToZonedTime(date, timeZone);
    },
    formatScore(score) {
      return score ? Math.round(score) + "%" : "--";
    },
    getHighestScore(ssId, scope) {
      let highest_score = null;
      const ss_highest_score = scope.row.highest_score;
      if (!ss_highest_score) {
        return "--";
      }
      ss_highest_score.forEach(ss => {
        if (ss["study_set_id"] == ssId) {
          highest_score = ss.highest_score;
          return false;
        }
      });
      return this.formatScore(highest_score);
    },
    getRAHighestScore(row, column) {
      if (row.highest_score && row.highest_score.length > 0) {
        return this.formatScore(row.highest_score[0][column]);
      }
      return "--";
    },
    getWarmUpSSScore(row) {
      if (this.studySets.length > 0 && row.submission_status == SUBMISSION_ON_TIME) {
        return this.formatScore(100);
      }
      return "--";
    },
    getCellClass() {
      return "el-table__column--custom";
    },
    sortName(a, b) {
      return a.user_name.toUpperCase().localeCompare(b.user_name.toUpperCase());
    },
    handleHelpTooltip(h, { column }) {
      return h("span", {}, [
        h(
          "el-tooltip",
          {
            attrs: {
              content: this.columnTooltipMap()[column.property],
              effect: "dark",
              placement: "top",
            },
          },
          [h("span", {}, [column.label])]
        ),
      ]);
    },
    columnTooltipMap() {
      return {
        score: this.$t("assignments.details.performance_tooltip.score"),
        date_complete: this.$t("assignments.details.performance_tooltip.date_complete"),
      };
    },
    handleHoveringCell(row) {
      this.hoveringId = row.user_id;
    },
    clearHoveringCell() {
      this.hoveringId = "";
    },
    canShowButtonHistoryScore(row) {
      const assignmentTypeShowHistory = ["read_aloud"];

      return (
        assignmentTypeShowHistory.includes(this.assignmentType) &&
        row.score &&
        row.score > 0 &&
        row.user_id === this.hoveringId
      );
    },
    handleClickHistory(row) {
      if (this.assignmentType === "ai_tutor") {
        this.handleShowUserAIHistoryScore(row);

        return;
      }

      this.handleShowUserHistoryScore(row);
    },
    async handleShowUserHistoryScore(row) {
      this.isModalLoading = true;
      this.selectedUserHistoryScore = {};
      this.selectedUser = row;
      try {
        this.selectedUserHistoryScore = await this.getUserAssignmentHistory({
          userId: row.user_id,
          assignmentId: this.currentAssignment.id,
        });
        this.isModalLoading = false;
        this.showUserHistoryScoreModal = true;
      } catch {
        this.isModalLoading = false;
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    async handleShowUserAIHistoryScore(row) {
      this.isModalLoading = true;
      this.selectedUserHistoryScore = {};
      this.selectedUser = row;
      try {
        //call to api

        this.isModalLoading = false;
        this.showUserHistoryScoreModal = true;
      } catch {
        this.isModalLoading = false;
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    setAssignmentDateAndRedirect(row) {
      this.setChartDate({
        startDate: this.formatDateWithTimezone(this.currentAssignment.start_date),
        endDate: this.formatDateWithTimezone(this.currentAssignment.due_date),
      });

      this.$router.push({
        name: "student-details",
        params: {
          organizationId: this.currentClass.organization_id,
          studentId: row.user_id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/_colors.scss";
#assignment-performance {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 32px 24px;
  margin-bottom: 24px;
}

.date-completed {
  display: flex;
  align-items: center;
  .date {
    width: 100px;
  }
  .tag-wrapper {
    min-width: 50px;
    margin-left: 10px;
  }
}

.score-cell {
  padding-left: 10px;
}

::v-deep .el-table {
  thead {
    text-transform: inherit;
  }
  .el-table__row {
    height: 80px;
    [class*="el-table_1_column"] {
      &.el-table__column--custom {
        border-top: none;
        .cell {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
    }
  }
}

.user-info {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  color: $darker-blue;
  .owner-info {
    p {
      font-weight: 500;
      font-size: 16px;
      text-transform: capitalize;
      color: $hyperlink;
      margin: 0;
      text-align: initial;
      display: flex;
      align-items: center;
      &:last-of-type {
        font-size: 14px;
        color: $light-blue-2;
        text-transform: none;
        line-height: 34px;
      }
    }
  }
}
</style>
